import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiFetch } from "./client";
import { useAuthStore } from "@/stores/auth";
import type {
  AuthResponse,
  ConfirmEmailChangeRequest,
  LoginRequest,
  RegisterRequest,
  RequestEmailChangeRequest,
  RequestResetRequest,
  ResetPasswordRequest,
  User,
} from "@/types/auth";

export const authKeys = {
  me: ["auth", "me"] as const,
  providers: ["auth", "providers"] as const,
};

export interface AuthProviders {
  google: boolean;
  require_invite: boolean;
}

export function useAuthProvidersQuery() {
  return useQuery({
    queryKey: authKeys.providers,
    queryFn: () => apiFetch<AuthProviders>("/auth/providers"),
    staleTime: Infinity,
  });
}

export function useMeQuery() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useQuery({
    queryKey: authKeys.me,
    queryFn: () => apiFetch<User>("/auth/me"),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLoginMutation() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LoginRequest) =>
      apiFetch<AuthResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: (data) => {
      setAuth(data.token, data.user);
      queryClient.setQueryData(authKeys.me, data.user);
    },
  });
}

export function useRegisterMutation() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RegisterRequest) =>
      apiFetch<AuthResponse>("/auth/register", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: (data) => {
      setAuth(data.token, data.user);
      queryClient.setQueryData(authKeys.me, data.user);
    },
  });
}

export function useLogoutMutation() {
  const logout = useAuthStore((s) => s.logout);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // No server-side logout needed — just clear local state
    },
    onSettled: () => {
      logout();
      queryClient.clear();
    },
  });
}

export function useRequestResetMutation() {
  return useMutation({
    mutationFn: (data: RequestResetRequest) =>
      apiFetch<{ ok: boolean }>("/auth/request-reset", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  });
}

export function useResetPasswordMutation() {
  return useMutation({
    mutationFn: (data: ResetPasswordRequest) =>
      apiFetch<{ ok: boolean }>("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  });
}

export function useRequestEmailChangeMutation() {
  return useMutation({
    mutationFn: (data: RequestEmailChangeRequest) =>
      apiFetch<{ ok: boolean }>("/auth/request-email-change", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  });
}

export function useConfirmEmailChangeMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ConfirmEmailChangeRequest) =>
      apiFetch<{ ok: boolean }>("/auth/confirm-email-change", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}
